import "./solicitor-case-details.scss";
import { P2Button } from "react-bootstrap-common-script";
import { TextSubheader } from "../../../components/text/text-subheader";
import ApplicationCooLayout from "../../../layout/application-coo-layout";
import { formatHdbRef } from "../../../utils/string-formatter";
import { CaseSummaryAccordion } from "../../../components/solicitor/case-details/case-summary/case-summary-accordion/case-summary-accordion";
import { TabsContainer } from "../../../components/solicitor/case-details/action-module/tabs-container/tabs-container";
import { useEffect, useMemo, useRef } from "react";
import { useMapDraft } from "../../../hooks/use-map-draft";
import { SMSInfo } from "../../../models/polymer/Properties.model";
import { SubmissionModal, SubmissionModalHandle } from "../../../components/solicitor/case-details/submissions/submission-modal/submission-modal";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import { useGetSolicitorCaseDetailQuery } from "../../../redux/services/getSolicitor";
import { useAppDispatch, useAppSelector } from "../../../redux/hook";
import { LottieLoader } from "../../../components/loader";
import { LoginSolicitorType } from "../../../models/enum/login-solicitor-type.enum";
import { SolicitorNotification } from "../../../components/solicitor/case-details/solicitor-notification/solicitor-notification";
import { SolicitorCaseDetailsProvider, useSolicitorCaseDetailsContext } from "../../../context/solicitor-case-details-context";
import { setApplicationId } from "../../../redux/slices/application-id-slice";
import useIsMobile from "../../../hooks/use-is-mobile";

function SolicitorCaseDetailsWithoutContext() {
  const queryParameters = new URLSearchParams(window.location.search);
  const applicationUuid = queryParameters.get("applicationUuid");

  // States
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const { uen } = useAppSelector((state) => state.auth.AuthenticationSlice.corpPassDetails);
  const applicationStep3FormData = useAppSelector((state) => state.app.ApplicationStep3Slice.formData);
  const AppDispatch = useAppDispatch();

  const { performDraftMapping } = useMapDraft();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();

  const submissionModalRef = useRef<SubmissionModalHandle>(null);

  const { data: detailData, isLoading: detailIsLoading, isError: detailIsError, error: detailError } = useGetSolicitorCaseDetailQuery(
    { uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? "", application_uuid: applicationUuid ?? "", uen },
    { skip: !loginUser || !singpassToken || !applicationUuid }
  );

  const { solicitorSubmissionsData, solicitorCompletionData, isCancelledOrRejected } = useSolicitorCaseDetailsContext();

  // Derived values
  const hdbRef = detailData?.hdb_ref;
  const isViewSubmissionsButtonEnabled = useMemo(() => {
    const hasDocumentSubmission =
      solicitorSubmissionsData &&
      ((solicitorSubmissionsData.message_info && solicitorSubmissionsData.message_info.length > 0) ||
        (solicitorSubmissionsData.folders && solicitorSubmissionsData.folders.length > 0) ||
        (solicitorSubmissionsData.additional_documents && solicitorSubmissionsData.additional_documents.length > 0) ||
        (solicitorSubmissionsData.grouped_additional_documents && solicitorSubmissionsData.grouped_additional_documents.length > 0));

    const hasCompletionData = solicitorCompletionData && (solicitorCompletionData.completion?.submitted_by || solicitorCompletionData.lodgement?.submitted_by);

    return hasDocumentSubmission || hasCompletionData;
  }, [solicitorSubmissionsData, solicitorCompletionData]);

  // Functions
  function handleViewSubmissions() {
    submissionModalRef.current?.setIsShowing(true);
  }

  // Effects
  if (!applicationUuid) {
    navigate(config.corppassRedirectUrl);
  }

  useEffect(() => {
    if (applicationUuid) AppDispatch(setApplicationId(applicationUuid));
  }, [applicationUuid]);

  useEffect(() => {
    if (!detailIsError || !detailError) return;

    const status = "status" in detailError ? detailError.status : undefined;
    if (status === 401 || status === 403) {
      navigate(config.corppassRedirectUrl);
    }
  }, [detailIsError, detailError, navigate]);

  useEffect(() => {
    if (detailData) {
      performDraftMapping(detailData, false, new SMSInfo());
    }
  }, [performDraftMapping, detailData]);

  const loginTypeArray = useMemo((): LoginSolicitorType[] => {
    let roles = []; //CO-T, //CO-L, //CO-O, //PO-T, //PO-L

    if (applicationStep3FormData.current.private_solicitor.uen === loginUser?.uinfin) {
      roles.push(LoginSolicitorType.CO_Transfer);
    }
    if (applicationStep3FormData.current.conveyancing_private_solicitor.uen === loginUser?.uinfin) {
      roles.push(LoginSolicitorType.CO_Loan);
    }

    if (applicationStep3FormData.proposed.private_solicitor.uen === loginUser?.uinfin) {
      roles.push(LoginSolicitorType.PO_Transfer);
    }
    if (applicationStep3FormData.proposed.conveyancing_private_solicitor.uen === loginUser?.uinfin) {
      roles.push(LoginSolicitorType.PO_Loan);
    }

    // NOTE: enable this once the ticket 797 implemented
    // if(applicationStep3FormData.outgoing.conveyancing_private_solicitor.uen === loginUser?.uinfin){
    //   roles.push(LoginSolicitorType.CO_Outgoing);
    // }

    return roles;
  }, [applicationStep3FormData, loginUser?.uinfin]);

  if (detailIsLoading) {
    return <LottieLoader />;
  }

  if (detailIsError) {
    const status = detailError && "status" in detailError ? detailError.status : undefined;
    if (status === 401 || status === 403) {
      return <LottieLoader />;
    }
  }

  return (
    <>
      <ApplicationCooLayout title={`Ref: ${formatHdbRef(hdbRef ?? "")}`} type="solicitor">
        <div className="polymer-application-coo__wrapper">
          <TextSubheader subheaderMain="Change in Flat Ownership" subheaderSub={detailData?.address} paddingLeftRight={42}>
            <P2Button
              disabled={!isViewSubmissionsButtonEnabled}
              onClick={handleViewSubmissions}
              variant={"outline-primary"}
              style={{ fontSize: "1rem", marginBottom: 16, ...(isMobile ? { width: "100%" } : {}) }}
            >
              View submissions
            </P2Button>
          </TextSubheader>
          <div className="polymer-case-summary__container">
            <div className="polymer-case-summary__content-container">
              <SolicitorNotification />
              <CaseSummaryAccordion applicationDetail={detailData} style={{ marginBottom: !isCancelledOrRejected ? 32 : 100 }} />
              {!isCancelledOrRejected ? <TabsContainer loginTypeArray={loginTypeArray} style={{ marginBottom: 100 }} /> : null}
            </div>
          </div>
        </div>
      </ApplicationCooLayout>
      <SubmissionModal ref={submissionModalRef} />
    </>
  );
}

export function SolicitorCaseSummary() {
  return (
    <SolicitorCaseDetailsProvider>
      <SolicitorCaseDetailsWithoutContext />
    </SolicitorCaseDetailsProvider>
  );
}
